import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users2, MapPin, Package, Loader2, ChevronRight } from "lucide-react";
import api from "@/lib/api";

// ---------------------------------------------------------------- per-tech tab

function TechDetail({ techId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["tech-analytics", techId],
    queryFn: () => api.get(`/api/admin/technicians/${techId}/analytics`).then((r) => r.data),
    enabled: !!techId,
  });

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-steel-light" /></div>;
  if (!data) return null;

  const { technician: tech, planned_jobs_next_week, suggested_pickup, consumption_history, pickup_formula } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-orange/10 text-orange grid place-items-center text-xl font-bold">
          {tech.name.charAt(0)}
        </div>
        <div>
          <h2 className="text-xl font-bold text-graphite">{tech.name}</h2>
          <p className="text-steel text-sm">{tech.company} · {tech.region}</p>
        </div>
        <Badge variant={tech.active ? "default" : "secondary"} className="ml-auto">
          {tech.active ? "Active" : "Inactive"}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-steel">Planned jobs next week</p>
            <p className="text-3xl font-bold text-graphite">{planned_jobs_next_week}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-steel">Total consumption records</p>
            <p className="text-3xl font-bold text-graphite">{consumption_history.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Suggested pickup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="w-4 h-4 text-orange" /> Suggested Pickup
          </CardTitle>
          <p className="text-xs text-steel font-mono bg-surface rounded px-2 py-1 mt-1">{pickup_formula}</p>
        </CardHeader>
        <CardContent>
          {suggested_pickup.length === 0 ? (
            <p className="text-steel text-sm">{planned_jobs_next_week === 0 ? "No jobs planned next week." : "No items to suggest."}</p>
          ) : (
            <div className="space-y-2">
              {suggested_pickup.map((item) => (
                <div key={item.sku} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-graphite truncate">{item.name}</p>
                    <p className="text-xs text-steel">{item.sku} · {item.brand}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-graphite">{item.suggested_qty} pcs</p>
                    <p className="text-xs text-steel">avg {item.avg_per_job}/job</p>
                  </div>
                  <Badge variant={item.is_in_stock ? "outline" : "destructive"} className="shrink-0 text-xs">
                    {item.is_in_stock ? "In Stock" : "Out"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent consumption */}
      <Card>
        <CardHeader><CardTitle className="text-base">Recent Consumption</CardTitle></CardHeader>
        <CardContent>
          {consumption_history.slice(0, 20).map((r) => (
            <div key={r.id} className="flex items-center gap-2 py-1.5 text-sm border-b border-border last:border-0">
              <span className="font-mono text-steel w-24 shrink-0">{r.sku}</span>
              <span className="flex-1 text-graphite truncate">{r.date}</span>
              <span className="font-bold text-graphite">×{r.qty}</span>
              {r.source_ref && <span className="text-xs text-steel truncate max-w-[80px]">{r.source_ref}</span>}
            </div>
          ))}
          {consumption_history.length === 0 && <p className="text-steel text-sm">No consumption records yet.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- per-region tab

function RegionView({ region }) {
  const { data, isLoading } = useQuery({
    queryKey: ["region-demand", region],
    queryFn: () => api.get(`/api/admin/regions/${region}/demand`).then((r) => r.data),
    enabled: !!region,
  });

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-steel-light" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-graphite">{region} — 2-Week Expected Demand</h2>
        <Badge variant="outline">{data.total_planned_jobs} planned jobs</Badge>
      </div>
      {data.demand.length === 0 ? (
        <p className="text-steel text-sm py-8 text-center">No demand data for this region yet.</p>
      ) : (
        <div className="space-y-2">
          {data.demand.map((item) => (
            <div key={item.sku} className="flex items-center gap-3 bg-surface rounded-lg px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-graphite truncate">{item.name}</p>
                <p className="text-xs text-steel">{item.sku} · {item.brand}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-graphite">{item.expected_qty_ceil} pcs</p>
                <p className="text-xs text-steel">({item.expected_qty} raw)</p>
              </div>
              <Badge variant={item.is_in_stock ? "outline" : "destructive"} className="shrink-0">
                {item.is_in_stock ? "Stock" : "Out"}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- main page

export default function Technicians() {
  const [selectedTechId, setSelectedTechId] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  const { data: techsData, isLoading: techsLoading } = useQuery({
    queryKey: ["admin-technicians"],
    queryFn: () => api.get("/api/admin/technicians").then((r) => r.data),
  });

  const { data: regionsData } = useQuery({
    queryKey: ["admin-regions"],
    queryFn: () => api.get("/api/admin/regions").then((r) => r.data),
  });

  const techs = techsData?.items || [];
  const regions = regionsData?.regions || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-graphite">Technicians</h1>
        <p className="text-steel text-sm mt-1">Field technician profiles, planned jobs, and parts consumption.</p>
      </div>

      <Tabs defaultValue="per-tech">
        <TabsList>
          <TabsTrigger value="per-tech"><Users2 className="w-4 h-4 mr-1.5" />Per Technician</TabsTrigger>
          <TabsTrigger value="per-region"><MapPin className="w-4 h-4 mr-1.5" />Per Region</TabsTrigger>
        </TabsList>

        <TabsContent value="per-tech" className="mt-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              {techsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-steel-light" /></div>
              ) : (
                <div className="space-y-1.5">
                  {techs.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTechId(t.id)}
                      className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                        selectedTechId === t.id
                          ? "bg-orange/10 border-orange/30 text-graphite"
                          : "bg-white border-border hover:bg-surface"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-orange/10 text-orange grid place-items-center font-bold text-sm shrink-0">
                        {t.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{t.name}</p>
                        <p className="text-xs text-steel">{t.region}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-steel-light shrink-0" />
                    </button>
                  ))}
                  {techs.length === 0 && (
                    <p className="text-steel text-sm py-4 text-center">No technicians yet. Import via FSM Import.</p>
                  )}
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              {selectedTechId ? (
                <TechDetail techId={selectedTechId} />
              ) : (
                <div className="flex items-center justify-center h-48 text-steel text-sm">
                  Select a technician to view details
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="per-region" className="mt-4">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="md:col-span-1">
              <div className="space-y-1.5">
                {regions.map((r) => (
                  <button
                    key={r}
                    onClick={() => setSelectedRegion(r)}
                    className={`w-full text-left flex items-center gap-2 px-4 py-3 rounded-xl border transition-colors ${
                      selectedRegion === r
                        ? "bg-orange/10 border-orange/30"
                        : "bg-white border-border hover:bg-surface"
                    }`}
                  >
                    <MapPin className="w-4 h-4 text-steel-light" />
                    <span className="font-medium text-sm">{r}</span>
                  </button>
                ))}
                {regions.length === 0 && <p className="text-steel text-sm py-4 text-center">No regions found.</p>}
              </div>
            </div>
            <div className="md:col-span-3">
              {selectedRegion ? (
                <RegionView region={selectedRegion} />
              ) : (
                <div className="flex items-center justify-center h-48 text-steel text-sm">
                  Select a region to view demand
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
